import type { LiveTruck } from '@chukua/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { LiveFeedConnection } from '../../../shared/components/ConnectionBadge';
import { parsePositionFeedEvent } from '../../../shared/policies/parse-position-feed-event';
import { listLiveTrucks, LIVE_MAP_STREAM_URL } from '../live-map.api';
import { applyPositionUpdate, mergeSnapshot } from '../policies/apply-position-update';

/** The snapshot is also the safety net: whatever a dropped stream, a rolled deploy or a missed message lost, a minute later it is right again. */
export const SNAPSHOT_REFRESH_MS = 60_000;

/** `EventSource.CLOSED`, spelled out because a test environment may have no `EventSource` to read it from. */
const CLOSED = 2;

export type LiveMapConnection = LiveFeedConnection;

export type LiveTrucksState = { status: 'loading' } | { status: 'error' } | { status: 'loaded'; trucks: LiveTruck[] };

/**
 * The trucks on the map (gitaumoses4/chukua#100): a snapshot of all of them, kept moving by the position stream. The stream is opened
 * first and the snapshot fetched on every `onopen` (the first connect and each reconnect), so nothing that happens between the two is
 * missed; a fix for a truck the snapshot has not listed yet is dropped (the next snapshot has it, with the registration and trip the stream does
 * lacks), and a fix is only ever replaced by a newer one. `EventSource`
 * reconnects by itself; `unavailable` is where there is none, and the snapshot alone keeps the page right every minute.
 */
export function useLiveTrucks(): { state: LiveTrucksState; connection: LiveMapConnection } {
  const [state, setState] = useState<LiveTrucksState>({ status: 'loading' });
  const [connection, setConnection] = useState<LiveMapConnection>(typeof EventSource === 'undefined' ? 'unavailable' : 'connecting');
  const trucksRef = useRef<LiveTruck[]>([]);

  const refreshSnapshot = useCallback(async () => {
    try {
      const snapshot = await listLiveTrucks();
      trucksRef.current = mergeSnapshot(trucksRef.current, snapshot);
      setState({ status: 'loaded', trucks: trucksRef.current });
    } catch {
      // A failed refresh keeps showing what is held; only the first load has nothing to keep.
      setState((held) => (held.status === 'loaded' ? held : { status: 'error' }));
    }
  }, []);

  useEffect(() => {
    let disposed = false;
    const snapshot = () => {
      if (!disposed) void refreshSnapshot();
    };
    const timer = setInterval(snapshot, SNAPSHOT_REFRESH_MS);
    if (typeof EventSource === 'undefined') {
      snapshot();
      return () => {
        disposed = true;
        clearInterval(timer);
      };
    }
    const source = new EventSource(LIVE_MAP_STREAM_URL);
    source.addEventListener('positions', (event) => {
      const parsed = parsePositionFeedEvent((event as MessageEvent<string>).data);
      if (!parsed) return;
      const next = applyPositionUpdate(trucksRef.current, parsed.positions);
      if (next === trucksRef.current) return;
      trucksRef.current = next;
      setState((held) => (held.status === 'loaded' ? { status: 'loaded', trucks: next } : held));
    });
    source.onopen = () => {
      setConnection('live');
      snapshot();
    };
    // A refused stream (no permission, an old server) is closed for good; a dropped one is retried by the browser.
    source.onerror = () => setConnection(source.readyState === CLOSED ? 'unavailable' : 'reconnecting');
    return () => {
      disposed = true;
      clearInterval(timer);
      source.close();
    };
  }, [refreshSnapshot]);

  return { state, connection };
}
